import { Injectable, inject } from '@angular/core';
import { CmsApiService } from './api/cms-api.service';
import { combineLatest, ReplaySubject } from 'rxjs';
import { CmsContentMapperService } from './cms-content-mapper.service';
import { map, tap } from 'rxjs/operators';
import { LanguageService } from './language.service';
import { ArrayService } from './array.service';
import { PaymentMode, UserPaymentModeService } from './user/user-payment-mode.service';
import { UserService } from './user/user.service';

export enum BankingType {
  DEPOSIT = 'deposit',
  WITHDRAW = 'cashout'
}

@Injectable({
  providedIn: 'root',
})
export class BankingService {

  private _api = inject(CmsApiService);
  private _mapper = inject(CmsContentMapperService);
  private _array = inject(ArrayService);
  private _user = inject(UserService);

  public paymentMode = inject(UserPaymentModeService);

  /**
   * Deposit payment list
   */
  public depositPaymentList$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * Withdrawal payment list
   */
  public withdrawalPaymentList$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * Order for segment sort deposit
   * @private
   */
  public orderPaymentsCrypto: string[] = [
    'BTC', 'BCH', 'ETH', 'LTC', 'DOG',
    'USDT', 'XRP', 'ADA', 'TRX', 'BNB',
  ].map(item => item + '_coinspaid');

  /**
   * Custom Footer Images list
   */
  private _customImagesForFooterPayments = ['devcode-applepay', 'devcode-picksellpay'];

  get type() {
    return BankingType;
  }

  getAllPayments() {
    return combineLatest([
      this.getPaymentMethodsByType('deposit', Number(this._user.auth)),
      this.getPaymentMethodsByType('cashout', Number(this._user.auth)),
      this.paymentMode.currentMode$,
    ]).pipe(
      map((list) => {
        return [
          list[2] === PaymentMode.CRYPTO ?
            list[0].filter(e => e.IsCrypto) :
            list[0].filter(e => !e.IsCrypto),
          list[2] === PaymentMode.CRYPTO ?
            list[1].filter(e => e.IsCrypto) :
            list[1].filter(e => !e.IsCrypto),
        ];
      }),
      tap((list) => {
        this.depositPaymentList$.next(list[0]);
        this.withdrawalPaymentList$.next(list[1]);
      }),
      map(([dep, cash]) => this._array.uniqueBy('id', [...dep, ...cash])),
    );
  }

  /**
   * Get payment methods from CMS by type
   */
  getPaymentMethodsByType(type: string, sort_auth_player: number) {
    return this._api.paymentList({ type, sort_auth_player }).pipe(
      map(response => this._mapper.mapCmsData(response.data, {
        id: 'id',
        slug: 'slug',
      })),
      map((list: any[]) => list.filter(e => e.id)),
      map((list: any[]) => list.map(item => {
        return {
          ...item,
          altImgSrc: `https://cdn.softswiss.net/logos/payments/color/${item.slug}.svg`,
          image: item.Image ? `/assets/svg/payments/${this.paymentMode.currentMode === PaymentMode.CRYPTO ? 'coinspaid/color' : 'fiat'}/coinspaid_coinspaid-86_${item.slug}.svg` :
            `https://cdn.softswiss.net/logos/payments/color/${item.slug}.svg`,
        };
      })),

      map((list) => {
        const resultArr = this._customImagesForFooterPayments
          .map((elem) => list.find((e) => e.slug === elem))
          .filter(e => !!e);
        if (resultArr[0]) {
          resultArr.map((e) => e.footerImg = `/assets/svg/payments/fiat/${e?.slug}-custom.svg`);
        }
        return list;
      }),
    );
  }
}
